import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Container, Row, Col } from 'react-bootstrap';
import { ArrowLeft, Eye, Inbox } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { dateToString } from '../../shared/utils/parseDate';
import { Pagination } from '../../features/Pagination';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';

// Компонент для отображения заявок на вакансию
const VacancySummariesPage = observer(() => {
  const { id: vacancyId } = useParams(); // Получение ID вакансии из URL
  const [vacancy, setVacancy] = useState(null);
  const [summaries, setSummaries] = useState({ items: [], totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const statusParam = searchParams.get('status');
  const appliedStatus =
    statusParam && !Number.isNaN(parseInt(statusParam)) ? parseInt(statusParam) : null;
  const [searchQuery, setSearchQuery] = useState(q);
  const [selectedStatus, setSelectedStatus] = useState(appliedStatus);
  const { offset, pageSize, setPage, page } = useUrlPagination(10);

  // Статусы заявок с переводом и цветом
  const statusMap = {
    1: { name: 'Активная', color: 'primary' },
    2: { name: 'Кандидат', color: 'info' },
    3: { name: 'Интервью', color: 'warning' },
    4: { name: 'Нанят', color: 'success' },
    5: { name: 'Не нанят', color: 'danger' },
    6: { name: 'Резерв', color: 'secondary' },
    7: { name: 'Отклонен', color: 'dark' },
  };

  // Черновики поиска и фильтра следуют за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchQuery(q);
  }, [q]);

  useEffect(() => {
    setSelectedStatus(appliedStatus);
  }, [appliedStatus]);

  // Загрузка заявок: одна точка входа, ответ устаревшего запроса не применяется
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequests.hr
      .getSummaries(vacancyId, appliedStatus, q || null, pageSize, offset)
      .then((response) => {
        if (cancelled) return;
        setSummaries({
          items: response.items || [],
          totalCount: response.totalCount || 0,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Ошибка при получении заявок:', error);
        toast.error('Не удалось загрузить заявки');
        setSummaries({ items: [], totalCount: 0 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vacancyId, appliedStatus, q, offset, pageSize]);

  // Получение данных вакансии
  useEffect(() => {
    apiRequests.hr
      .getById(vacancyId)
      .then(setVacancy)
      .catch((error) => {
        console.error('Ошибка при получении данных вакансии:', error);
        toast.error('Не удалось загрузить информацию о вакансии');
      });
  }, [vacancyId]);

  const updateParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') params.set(key, String(value));
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  // Обработчик поиска
  const handleSearch = () => updateParams({ q: searchQuery.trim() });

  // Обработчик изменения фильтра
  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setSelectedStatus(value === null ? null : parseInt(value));
    }
  };

  // Применить фильтры
  const applyFilters = () => updateParams({ status: selectedStatus });

  // Сбросить фильтры
  const resetFilters = () => {
    setSelectedStatus(null);
    setSearchQuery('');
    updateParams({ status: null, q: null });
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              Заявки на вакансию{' '}
              {vacancy ? <span className="text-primary">«{vacancy.title}»</span> : ''}
            </h2>
          </div>

          <PageBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={handleSearch}
            searchPlaceholder="Поиск по имени или информации..."
            filters={[
              {
                key: 'status',
                type: 'select',
                label: 'Статус',
                value: selectedStatus === null ? null : String(selectedStatus),
                placeholder: 'Все статусы',
                options: Object.entries(statusMap).map(([key, value]) => ({
                  value: key,
                  label: value.name,
                })),
              },
            ]}
            onFilterChange={handleFilterChange}
            onApplyFilters={applyFilters}
            onResetFilters={resetFilters}
            rightContent={
              <Link to={`/HR/vacancies/${vacancyId}`} className="page-bar-clean__link-btn">
                <ArrowLeft size={18} />
                <span>К вакансии</span>
              </Link>
            }
          />
        </Col>
      </Row>

      {/* Список заявок */}
      <Row>
        {loading ? (
          <Col className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
          </Col>
        ) : summaries.items.length === 0 ? (
          <Col xs={12}>
            {q || appliedStatus ? (
              <EmptyState
                icon={Inbox}
                variant="search"
                title="Заявок не найдено"
                description="Под запрос и выбранный статус заявок нет. Измените формулировку или сбросьте фильтры."
              />
            ) : (
              <EmptyState
                icon={Inbox}
                title="Откликов пока нет"
                description="Когда кандидаты откликнутся на эту вакансию на сайте, их заявки появятся здесь."
              />
            )}
          </Col>
        ) : (
          <>
            <Col xs={12}>
              <div className="mb-3">
                {summaries.items.map((summary) => {
                  const statusInfo = statusMap[summary.status] || {
                    name: 'Активная',
                    color: 'primary',
                  };
                  return (
                    <EntityCard
                      key={summary.id}
                      image={`https://ui-avatars.com/api/?name=${summary.name?.[0] || 'U'}&color=7F9CF5&background=EBF4FF`}
                      title={`${summary.name} ${summary.surname}`}
                      subtitle={summary.aboutInfo || 'Без описания'}
                      badge={{ text: statusInfo.name, variant: statusInfo.color }}
                      fields={[{ label: 'Создано', value: dateToString(summary.createdAt) }]}
                      actions={
                        <Link
                          to={`/HR/vacancies/${vacancyId}/summaries/${summary.id}`}
                          className="entity-card__action"
                          title="Подробнее"
                        >
                          <Eye size={20} />
                        </Link>
                      }
                    />
                  );
                })}
              </div>
            </Col>

            <Col xs={12} className="mt-4">
              <Pagination
                page={page}
                total={summaries.totalCount}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            </Col>
          </>
        )}
      </Row>
    </Container>
  );
});

export { VacancySummariesPage };
